// "Does this device hold that flag, and what does it say it is?"
//
// Asked of the device itself, over MQTT, because the two wiring-correction
// flags (`ReverseAction` for a curtain, `SwapWarm` for a white light) now live
// in the DEVICE as well as in the database — see core/devices/commands.js — and
// three things need the device's own answer rather than the column:
//
//   1. **Did the save reach the hardware?** A column write is not a correction
//      until the chip has it. Same rule as a door-lock guest code: report what
//      the device said, never "Saved" on a publish that nothing acknowledged
//      (core/devices/lockPasswords).
//   2. **Which side is doing the swap?** For a white light the app has been
//      exchanging the two channels on the wire since 2026-10-18, and firmware
//      that swaps them as well would cancel it out — the fixture the feature
//      exists to fix would invert. The app keeps swapping ONLY for firmware
//      that cannot (core/devices/lightReverse `appMustSwap`).
//   3. **What is actually in the chip's EEPROM**, for the bench.
//
// ⚠️ THE REPLY MUST CARRY AN `=`, OR AN ECHO READS AS AN ANSWER. Every firmware
// publishes some commands straight back on `<uno>/client`, and "GETSWAPWARM"
// echoed contains the word "SWAPWARM" — matching the name alone would report
// capability on exactly the firmware that has none. The firmwares answer
// `SwapWarm = 1 (…) | EEPROM[14]=0x6B`, so the `=` and the digit are the test.
//
// ⚠️ SILENCE IS NOT "NO". A device that is offline, or busy, answers nothing —
// and that is indistinguishable from old firmware. So `answered: false` means
// "could not be established", and every caller must treat it as the SAFE
// default for its own question, which is not the same default in both cases:
// for a light, "the chip does not swap" (so the app must), and for the curtain
// toggle, "not confirmed" (so say so rather than claiming a save).

import {
  connectMqtt, subscribeTopic, unsubscribeTopic, addMessageListener,
  publishMessage, waitForConnect,
} from '../mqtt/mqttClient';
import { publishCommand } from '../utils/deviceController';

// The same window the status dot uses, and for the same reason: measured
// replies run to 3.4 s alone and 9.6 s in a burst, so a shorter wait reports a
// healthy device as silent. A reply ends the wait at once. See
// core/devices/liveness PROBE_WINDOW_MS.
export const FLAG_WINDOW_MS = 8000;

/**
 * Pull `<name> = <0|1>` out of a device reply. -> 0 | 1 | null
 *
 * Exported for the tests and for anything reading the unsolicited status line;
 * see the `=` note above for why the name alone is not enough.
 */
export function parseFlagReply(payload, name) {
  const body = String(payload ?? '');
  const m = body.match(new RegExp(`${name}\\s*=\\s*([01])`, 'i'));
  return m ? Number(m[1]) : null;
}

/**
 * Ask one device for one flag. -> { answered, value, text }
 *
 * `userId` is passed when the word is a SET, because a write wants the normal
 * `#<userId>` envelope that every other command carries; a plain read is sent
 * bare so it cannot be mistaken for somebody's action in the device log.
 */
export function askDeviceFlag(uniqueNumber, word, name, {
  windowMs = FLAG_WINDOW_MS, userId = null,
} = {}) {
  const uno = String(uniqueNumber || '').trim();
  const miss = { answered: false, value: null, text: '' };
  if (!uno || !word) return Promise.resolve(miss);

  connectMqtt();
  return waitForConnect().then((ok) => {
    if (!ok) return miss;
    const topic = `${uno}/client`;
    return new Promise((resolve) => {
      let done = false;
      let timer = null;
      let detach = () => {};
      const finish = (r) => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        detach();
        unsubscribeTopic(topic);
        resolve(r);
      };
      // Listen before publishing — a device on the bench answers in 0.1 s, well
      // inside the time a subscribe takes to round-trip.
      detach = addMessageListener((t, payload) => {
        if (String(t) !== topic) return;
        const value = parseFlagReply(payload, name);
        if (value !== null) finish({ answered: true, value, text: String(payload) });
      });
      subscribeTopic(topic);
      if (userId == null) publishMessage(`${uno}/device`, word);
      else publishCommand(uno, word, userId);
      timer = setTimeout(() => finish(miss), windowMs);
    });
  }).catch(() => miss);
}

/**
 * Set a flag in the device and read it back. -> { answered, value, text }
 *
 * ⚠️ TWO WORDS, NOT ONE, BECAUSE THE SET DOES NOT ANSWER WITH A VALUE. The
 * firmware already in the field replies to `REVERSEACTIONON` with
 * "Reverse Action ON (open/close relays swapped)" — no `=`, so nothing can be
 * read out of it (and by the rule above, nothing should be). The GET that
 * follows is the receipt, and it has the further merit of reporting what the
 * chip ACTUALLY holds rather than what it was told.
 *
 * It is also why this works on every relay curtain today with no firmware
 * change at all: `GETREVERSEACTION` has been answered since OtaForTuya revived
 * the flag.
 */
export async function setDeviceFlag(uniqueNumber, setWord, getWord, name, {
  windowMs = FLAG_WINDOW_MS, userId = null,
} = {}) {
  const uno = String(uniqueNumber || '').trim();
  const miss = { answered: false, value: null, text: '' };
  if (!uno) return miss;
  // ⚠️ CONNECTED FIRST. Publishing to a disconnected client is not an error —
  // the command simply vanishes (core/offline/offlineMode), and the GET that
  // followed would then report the OLD value as if the write had been refused.
  connectMqtt();
  if (!(await waitForConnect())) return miss;
  // Fire and forget: the SET's own reply is not a value (see above), and waiting
  // for it would double the time before the receipt is asked for.
  if (userId == null) publishMessage(`${uno}/device`, setWord);
  else publishCommand(uno, setWord, userId);
  return askDeviceFlag(uno, getWord, name, { windowMs });
}
